import { create } from 'zustand';

import { SECURE_API } from 'src/api/api';
import { LanguageKeys } from 'src/common/constants/languages';
import {
  IMyRegistration,
  IRegistration,
  IRegistrationAdminCreatePayload,
} from 'src/interfaces/Registration';
import { runApiAction } from 'src/stores/lib/runApiAction';

interface RegistrationsState {
  registrations: IMyRegistration[];
  isLoading: boolean;
  error: unknown | null;
  fetchMyRegistrations: () => Promise<void>;
  createRegistration: (
    payload: Pick<IRegistration, 'participantId' | 'editionId' | 'competitionId' | 'distanceId'>,
    lang?: LanguageKeys,
  ) => Promise<void>;
  updateRegistration: (
    id: string,
    payload: Pick<IRegistration, 'competitionId' | 'distanceId'>,
  ) => Promise<void>;
  createRegistrationAsAdmin: (payload: IRegistrationAdminCreatePayload, lang?: LanguageKeys) => Promise<void>;
  updateRegistrationAsAdmin: (
    id: string,
    payload: Partial<Pick<IRegistration, 'competitionId' | 'distanceId'>> & { price?: number },
  ) => Promise<void>;
  deleteRegistration: (id: string) => Promise<void>;
  deleteRegistrationAsAdmin: (id: string) => Promise<void>;
}

const useRegistrationsStore = create<RegistrationsState>(set => {
  const fetchMyRegistrations = async () => {
    await runApiAction({
      set,
      setLoading: true,
      clearLoadingOnFinish: true,
      action: () => SECURE_API.get<IMyRegistration[]>('/me/registrations'),
      onSuccess: ({ data }) => {
        set({ registrations: data, error: null });
      },
      onError: error => {
        set({ registrations: [], error });
      },
    });
  };

  const runRegistrationMutation = async (action: () => Promise<unknown>) => {
    set({ isLoading: true, error: null });
    const result = await runApiAction({
      set,
      action,
      onError: error => {
        set({ error });
      },
    });

    if (result === undefined) {
      set({ isLoading: false });
      return false;
    }

    await fetchMyRegistrations();
    return true;
  };

  const runAdminMutation = async (action: () => Promise<unknown>) => {
    await runApiAction({
      set,
      setLoading: true,
      clearLoadingOnFinish: true,
      action,
      onSuccess: () => {
        set({ error: null });
      },
      onError: error => {
        set({ error });
      },
    });
  };

  return {
    registrations: [],
    isLoading: false,
    error: null,

    fetchMyRegistrations,

    createRegistration: async (payload, lang = LanguageKeys.ro) => {
      await runRegistrationMutation(() =>
        SECURE_API.post(`/me/registrations?lang=${lang}`, payload),
      );
    },

    updateRegistration: async (id, payload) => {
      await runRegistrationMutation(() => SECURE_API.patch(`/me/registrations/${id}`, payload));
    },

    createRegistrationAsAdmin: async (payload, lang = LanguageKeys.ro) => {
      await runAdminMutation(() => SECURE_API.post(`/registrations?lang=${lang}`, payload));
    },

    updateRegistrationAsAdmin: async (id, payload) => {
      await runAdminMutation(() => SECURE_API.patch(`/registrations/${id}`, payload));
    },

    deleteRegistration: async id => {
      await runRegistrationMutation(() => SECURE_API.delete(`/me/registrations/${id}`));
    },

    deleteRegistrationAsAdmin: async id => {
      await runAdminMutation(() => SECURE_API.delete(`/registrations/${id}`));
    },
  };
});

export default useRegistrationsStore;
